/**
 * The `monitor` tool's scripts: esp-idf-monitor's non-interactive command
 * mode, run against the device pane's board.
 *
 * A script is lines of commands in esp-idf-monitor's syntax. The whole script
 * is parsed before anything runs, so a typo fails the call instead of
 * stopping it halfway with the board in an unknown state. `expect` matches
 * lines as esp-idf-monitor does: output not yet returned to the caller is
 * scanned first and consumed up to the match, then new output as it arrives,
 * including an unfinished line such as a console prompt. Unlike
 * esp-idf-monitor, `reset`, `flash` and the ROM's boot line of a restart drop
 * the output not yet matched, since it came from the run before them.
 */

export type MonitorCommand =
    | { kind: "reset" }
    | { kind: "flash" }
    | { kind: "send"; text: string }
    | { kind: "sleep"; seconds: number }
    | { kind: "expect"; pattern: RegExp; timeoutSeconds: number | undefined }
    | { kind: "exit" };

export interface ScriptLine {
    /** The line as written, for the transcript. */
    text: string;
    command: MonitorCommand;
}

/** What the script needs from the device pane. */
export interface MonitorBoard {
    reset(): Promise<void>;
    flash(): Promise<void>;
    /** Write `text` to the board's console. */
    send(text: string): Promise<void>;
    /** Show a line of the script's progress in the pane's terminal. */
    note(line: string): void;
    /** Call `listener` with each piece of board output until the returned function is called. */
    watchSerial(listener: (text: string) => void): () => void;
    /** The output not yet returned by read_serial, which it then no longer returns. */
    takeSerialOutput(): string;
}

export interface MonitorOutcome {
    ok: boolean;
    /** How the script ended, in one sentence. */
    summary: string;
    /** Output from before the script that read_serial had not returned. */
    earlier: string;
    /**
     * The board's output during the script, and a `--- ` line with the time
     * and the command at the first line end after each command started.
     */
    transcript: string;
}

/** How much output `expect` keeps to scan; esp-idf-monitor keeps 1024 lines. */
const MATCH_KEPT = 64 * 1024;

/**
 * What esp-idf-monitor ends a sent line with for a chip, and what ESP-IDF's
 * stdin turns into a line end by default.
 */
const EOL = "\r";

const ANSI_ESCAPE = /\x1b\[[0-9;]*[A-Za-z]/g;

/** The line every chip's ROM prints first after a reset, e.g. "rst:0xc (SW_CPU),boot:0xc (...)". */
const BOOT_LINE = /^rst:0x[0-9a-f]+ \(/;

/**
 * Reset reasons of a reset from outside the chip: the EN pin, which the
 * serial adapter drives, and the USB Serial/JTAG peripheral's reset. Any other
 * reason (SW_CPU, a watchdog, brownout) means the board restarted by itself.
 */
const HOST_RESET = /^rst:0x[0-9a-f]+ \((POWERON|USB_UART)/;

export function parseMonitorScript(script: string): ScriptLine[] {
    const lines: ScriptLine[] = [];
    script.split(/\r?\n/).forEach((raw, index) => {
        const text = raw.trim();
        if (text === "" || text.startsWith("#")) return;
        try {
            lines.push({ text, command: parseCommand(raw.replace(/^\s+/, "")) });
        } catch (e) {
            throw new Error(`Line ${index + 1} (${text}): ${e instanceof Error ? e.message : e}`);
        }
    });
    return lines;
}

function parseCommand(line: string): MonitorCommand {
    const space = line.indexOf(" ");
    const name = (space < 0 ? line : line.slice(0, space)).toLowerCase();
    // `send` keeps its argument as written, trailing spaces included.
    const argument = space < 0 ? "" : line.slice(space + 1);
    switch (name) {
        case "reset":
        case "flash":
        case "exit":
            if (argument.trim() !== "") throw new Error(`${name} takes no argument.`);
            return { kind: name };
        case "send":
            return { kind: "send", text: argument };
        case "sleep":
            return { kind: "sleep", seconds: positive(argument.trim(), "sleep") };
        case "expect":
            return parseExpect(argument.trim());
        case "flash-all":
        case "app-flash":
            throw new Error("use flash, which writes all the images of the last build.");
        case "output":
        case "log":
        case "timestamps":
        case "bootloader":
            throw new Error(`${name} is not supported here.`);
        default:
            throw new Error(`unknown command ${name}.`);
    }
}

function parseExpect(argument: string): MonitorCommand {
    let timeoutSeconds: number | undefined;
    let source = argument;
    if (argument.startsWith("--timeout")) {
        const match = /^--timeout\s+(\S+)\s+(.*)$/.exec(argument);
        if (match === null) throw new Error("expect --timeout needs <seconds> and a <regex>.");
        timeoutSeconds = positive(match[1]!, "the expect timeout");
        source = match[2]!;
    }
    if (source === "") throw new Error("expect needs a <regex>.");
    return { kind: "expect", pattern: parsePattern(source), timeoutSeconds };
}

/** A regex as esp-idf-monitor's scripts write it, where a leading (?i) ignores case. */
export function parsePattern(source: string): RegExp {
    // Python's leading (?i), which JavaScript has as a flag.
    const ignoreCase = source.startsWith("(?i)");
    if (ignoreCase) source = source.slice(4);
    try {
        return new RegExp(source, ignoreCase ? "i" : "");
    } catch (e) {
        throw new Error(`invalid regex: ${e instanceof Error ? e.message : e}`);
    }
}

function positive(text: string, what: string): number {
    const value = Number(text);
    if (text === "" || !Number.isFinite(value) || value <= 0) {
        throw new Error(`${what} needs a number of seconds above 0, not "${text}".`);
    }
    return value;
}

/**
 * Board output that `expect` has not consumed. Lines are matched without
 * their line end and without color escapes; the unfinished last line is
 * matched as it stands.
 */
export class SerialMatcher {
    #text = "";

    /** Add output, and return the ROM boot lines it completes, dropping the output before them. */
    push(text: string): string[] {
        let at = this.#text.lastIndexOf("\n") + 1;
        this.#text += text.replace(ANSI_ESCAPE, "");
        const boots: string[] = [];
        for (let end = this.#text.indexOf("\n", at); end >= 0; end = this.#text.indexOf("\n", at)) {
            const line = this.#text.slice(at, end).replace(/\r$/, "");
            if (BOOT_LINE.test(line)) {
                boots.push(line);
                this.#text = this.#text.slice(at);
                end -= at;
            }
            at = end + 1;
        }
        if (this.#text.length > MATCH_KEPT) {
            const cut = this.#text.indexOf("\n", this.#text.length - MATCH_KEPT);
            this.#text = cut < 0 ? this.#text.slice(-MATCH_KEPT) : this.#text.slice(cut + 1);
        }
        return boots;
    }

    clear(): void {
        this.#text = "";
    }

    /** The first line that matches, consuming the output through it. */
    take(pattern: RegExp): string | undefined {
        const lines = this.#text.split("\n");
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i]!.replace(/\r$/, "");
            if (i === lines.length - 1 && line === "") break;
            if (!pattern.test(line)) continue;
            this.#text = lines.slice(i + 1).join("\n");
            return line;
        }
        return undefined;
    }
}

export async function runMonitorScript(
    lines: ScriptLine[],
    board: MonitorBoard,
    timeoutMs: number,
    progress: (message: string) => void = () => {},
): Promise<MonitorOutcome> {
    const deadline = Date.now() + timeoutMs;
    const matcher = new SerialMatcher();
    const earlier = board.takeSerialOutput();
    matcher.push(earlier);
    const started = Date.now();
    let transcript = "";
    /** Command lines waiting for the board to end the line it is in. */
    let marks = "";
    let arrived: (() => void) | undefined;
    const restarts: string[] = [];
    const stopWatching = board.watchSerial((text) => {
        const end = marks === "" ? -1 : text.indexOf("\n");
        if (end < 0) {
            transcript += text;
        } else {
            transcript += text.slice(0, end + 1) + marks + text.slice(end + 1);
            marks = "";
        }
        for (const boot of matcher.push(text)) {
            if (HOST_RESET.test(boot)) continue;
            restarts.push(boot);
            mark(`the board restarted by itself: ${boot}`);
        }
        arrived?.();
    });
    const mark = (line: string): void => {
        const seconds = ((Date.now() - started) / 1000).toFixed(2);
        const entry = `--- ${seconds} s: ${line}\n`;
        if (transcript === "" || transcript.endsWith("\n")) transcript += entry;
        else marks += entry;
        board.note(line);
    };
    /** Resolves when output arrives or at `until`, whichever is first. */
    const wait = (until: number): Promise<void> =>
        new Promise((resolve) => {
            const timer = setTimeout(done, Math.max(0, until - Date.now()));
            function done(): void {
                clearTimeout(timer);
                arrived = undefined;
                resolve();
            }
            arrived = done;
        });

    const end = (ok: boolean, outcome: string): MonitorOutcome => {
        stopWatching();
        const summary =
            restarts.length === 0
                ? outcome
                : `${outcome} The board restarted by itself ${restarts.length} time(s) during` +
                  ` the script, first with ${restarts[0]}.`;
        // What the transcript returns, read_serial should not return again.
        board.takeSerialOutput();
        if (!ok) mark(summary);
        if (marks !== "") transcript += `\n${marks}`;
        const plain = (text: string) => text.replace(ANSI_ESCAPE, "");
        return { ok, summary, earlier: plain(earlier), transcript: plain(transcript) };
    };
    const outOfTime = (): MonitorOutcome =>
        end(false, `The script was stopped after ${timeoutMs / 1000} s, its time limit.`);

    for (const [index, line] of lines.entries()) {
        if (Date.now() >= deadline) return outOfTime();
        mark(line.text);
        progress(line.text);
        const command = line.command;
        try {
            switch (command.kind) {
                case "reset":
                case "flash":
                    matcher.clear();
                    await (command.kind === "reset" ? board.reset() : board.flash());
                    break;
                case "send":
                    await board.send(command.text + EOL);
                    break;
                case "sleep": {
                    const wanted = Date.now() + command.seconds * 1000;
                    const until = Math.min(wanted, deadline);
                    while (Date.now() < until) await wait(until);
                    if (wanted > deadline) return outOfTime();
                    break;
                }
                case "expect": {
                    const limit = command.timeoutSeconds;
                    const wanted = limit === undefined ? Infinity : Date.now() + limit * 1000;
                    const until = Math.min(wanted, deadline);
                    let found = matcher.take(command.pattern);
                    while (found === undefined && Date.now() < until) {
                        await wait(until);
                        found = matcher.take(command.pattern);
                    }
                    if (found === undefined) {
                        if (wanted > deadline) return outOfTime();
                        return end(
                            false,
                            `Line ${index + 1}: ${command.pattern} did not appear within ${limit} s` +
                                " in output not returned by an earlier call; the rest of the" +
                                " script was not run.",
                        );
                    }
                    break;
                }
                case "exit":
                    return end(true, "The script exited.");
            }
        } catch (e) {
            const why = e instanceof Error ? e.message : String(e);
            return end(false, `Line ${index + 1} (${line.text}) failed: ${why}`);
        }
    }
    return end(true, "The script ran to its end.");
}
